package com.taocent.simple.compose.component.richtext.core.internal.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.contextmenu.provider.LocalTextContextMenuDropdownProvider
import androidx.compose.foundation.text.contextmenu.provider.LocalTextContextMenuToolbarProvider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalClipboard
import androidx.compose.ui.platform.LocalTextToolbar
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.IntOffset
import com.taocent.simple.compose.component.richtext.core.RichTextConfig
import com.taocent.simple.compose.component.richtext.core.RichTextState
import com.taocent.simple.compose.component.richtext.core.LocalRichTextConfig
import com.taocent.simple.compose.component.richtext.core.platform.rememberRichTextPlatformAdapter
import com.taocent.simple.compose.component.richtext.core.platform.suppressNativeContextMenu
import com.taocent.simple.compose.component.richtext.core.internal.format.ParagraphStyleUtils
import com.taocent.simple.compose.component.richtext.core.internal.platform.InterceptImePasteTextInput
import com.taocent.simple.compose.component.richtext.core.internal.toolbar.FloatingToolbarActiveInfo
import com.taocent.simple.compose.component.richtext.core.internal.toolbar.FloatingToolbarHostState
import com.taocent.simple.compose.component.richtext.core.internal.toolbar.LocalFloatingToolbarHost
import kotlinx.coroutines.delay

/**
 * RichTextTextField 的共享核心实现 — 取代 :richtext 与 :blockrichtext 中两份
 * 几乎逐行重复的 RichTextTextField.kt。
 *
 * 本 Composable 负责:
 * - 选区 / Pointer / Android collapsed-cursor / suppress native context menu 等共有的
 *   文本字段行为。
 * - 选中态变化时把 [FloatingToolbarActiveInfo] 上报给 [LocalFloatingToolbarHost]
 *   (若调用方注入了 host),并在该 host 离开组合时清理。
 * - 渲染 [RichTextBasicTextField],统一通过 [handleRichTextKeyEvent] 处理快捷键。
 *
 * 本 Composable **不**渲染 FloatingToolbar 本身 —— 工具栏的渲染位置由调用方决定:
 * - :richtext 旧版 (com.taocent.simple.compose.component.richtext.RichTextTextField)
 *   在本 Composable 之外的 Box 中渲染本地 toolbar(与 TextField 同一上下文,行为
 *   等同于 host.active 直接驱动,完全一致)。
 * - :blockrichtext 旧版 (com.taocent.simple.compose.component.blockrichtext.RichTextTextField)
 *   由顶层 BlockRichTextEditor 持有 host,统一渲染。
 *
 * 这样:
 * - 选区/键盘/手势逻辑只有一份实现(消除两份 RichTextTextField 80% 重复)。
 * - blockrichtext 修正过的 `selection.min` 上沿 anchor 行为反向同步到 richtext。
 *
 * @param dispatchRegisteredCommands  是否把快捷键派发给 [com.taocent.simple.compose.component.richtext.core.KeyCommandRegistry]
 *                                    (richtext 模块打开以支持自定义快捷键;blockrichtext 关闭,由顶层拦截 Ctrl+V)。
 * @param onUserValueChange  消费拦截回调,返回 true 表示已处理,Core 不会调用 [RichTextState.onValueChange]。
 * @param onFocusChanged     焦点变化回调,调用方可用于联动自己的 UI 状态。
 */
@OptIn(com.taocent.simple.compose.component.richtext.core.InternalRichTextApi::class)
@Composable
fun RichTextTextFieldCore(
    state: RichTextState,
    modifier: Modifier = Modifier,
    placeholder: String = "请输入文本...",
    focusRequester: FocusRequester = remember { FocusRequester() },
    onUserValueChange: ((old: TextFieldValue, new: TextFieldValue) -> Boolean)? = null,
    onUndo: (() -> Unit)? = null,
    onRedo: (() -> Unit)? = null,
    dispatchRegisteredCommands: Boolean = true,
    onFocusChanged: (Boolean) -> Unit = {},
    onTextLayout: (TextLayoutResult) -> Unit = {},
    floatingToolbarHost: FloatingToolbarHostState? = LocalFloatingToolbarHost.current,
    config: RichTextConfig = LocalRichTextConfig.current,
) {
    val clipboard = LocalClipboard.current
    val platformAdapter = rememberRichTextPlatformAdapter()
    val coroutineScope = rememberCoroutineScope()
    var isDragging by remember { mutableStateOf(false) }
    var showingContextToolbar by remember { mutableStateOf(false) }
    var androidLastCollapsedCursor by remember { mutableStateOf<Int?>(null) }
    var androidSameCursorTapCount by remember { mutableStateOf(0) }
    // 提升到 RichTextState.suppressedToolbarSelection,以便顶层 BlockRichTextEditor
    // 的 onCopy / onCut / onPaste / onSelectAll 路径在工具栏按钮被点击后
    // 也能主动设置该标志,实现"再点一次收起浮动工具栏"。
    // 区分"用户主动拖动产生的非折叠选区"与"BasicTextField 长按 word selection 产生的非折叠选区":
    // - 用户主动拖动选区(isDragging 期间发生过 collapsed → collapsed 的光标移动)时,工具栏应显示
    // - 长按 word selection(BasicTextField 自己把 collapsed 一次性变成 non-collapsed)时,
    //   如果用户已经主动 hide 了工具栏(短按同位置),我们不希望 LaunchedEffect 再次把工具栏顶出来
    var userDraggedSelection by remember { mutableStateOf(false) }
    var textLayoutResult by remember { mutableStateOf<TextLayoutResult?>(null) }
    var textFieldGlobalPosition by remember { mutableStateOf(IntOffset.Zero) }
    var textFieldWidthPx by remember { mutableStateOf(0) }
    var textFieldHeightPx by remember { mutableStateOf(0) }
    var isTextFieldFocused by remember { mutableStateOf(false) }

    fun toolbarAnchor(): IntOffset {
        val layout = textLayoutResult ?: return IntOffset.Zero
        val textLength = layout.layoutInput.text.length
        val selection = state.textFieldValue.selection
        // 工具栏应显示在选区"上沿"之上:
        // - collapsed(纯光标):用光标位置,该位置所在行的顶部作为 anchor.y;
        // - non-collapsed:用 selection.min(选区的"上沿"端),无论用户从哪边拖,
        //   min 都是选区在屏幕上更靠上的那一端,符合 iOS/Android 系统工具栏行为。
        // anchor.y 必须是**行顶**而不是行底,这样 PopupPositionProvider 用
        // `anchorTop - popupHeight - topPadding` 算出的工具栏底才能落在行顶之上,
        // 即"工具栏位于文字上方"。
        val cursorPos = if (selection.collapsed) selection.start else selection.min
        val cursorRect = layout.getCursorRect(cursorPos.coerceIn(0, textLength))
        return IntOffset(
            x = ((cursorRect.left + cursorRect.right) / 2).toInt(),
            y = cursorRect.top.toInt(),
        )
    }

    fun activeInfo(): FloatingToolbarActiveInfo {
        return FloatingToolbarActiveInfo(
            state = state,
            position = textFieldGlobalPosition,
            widthPx = textFieldWidthPx,
            heightPx = textFieldHeightPx,
            anchor = toolbarAnchor(),
            isDragging = false,
        )
    }

    val clipboardActions = remember(state, clipboard, config) {
        RichTextClipboardActions(clipboard, state, config)
    }

    LaunchedEffect(
        state.textFieldValue.selection,
        isDragging,
        showingContextToolbar,
        floatingToolbarHost,
        userDraggedSelection,
        state.suppressedToolbarSelection
    ) {
        val host = floatingToolbarHost ?: return@LaunchedEffect
        if (isDragging) {
            showingContextToolbar = false
            host.clearActive(state)
        } else if (!state.textFieldValue.selection.collapsed) {
            // 不要 reset showingContextToolbar:
            // 长按后 BasicTextField 内部可能把 selection 从 collapsed 变成非折叠
            // (开始 word selection),但此时 showingContextToolbar 仍然代表"工具栏应该显示",
            // 必须保留,否则 hide→show 的 toggle 会因为标志被清零而失效。
            delay(config.floatingToolbar.showDelayMs)
            // 延时期间选区可能已经塌缩或切换到其他块,丢弃上报
            if (state.textFieldValue.selection.collapsed) return@LaunchedEffect
            val sel = state.textFieldValue.selection
            val annotated = state.textFieldValue.annotatedString
            val urlAnnotations = annotated.getStringAnnotations("URL", sel.min, sel.max)
            val isEntireHyperlink = urlAnnotations.any { it.start <= sel.min && it.end >= sel.max }
            if (isEntireHyperlink) {
                host.clearActive(state)
            } else if (userDraggedSelection || showingContextToolbar || state.suppressedToolbarSelection != sel) {
                // 用户主动拖动产生的非折叠选区 → 应该显示工具栏
                host.reportActive(activeInfo())
            } else if (!showingContextToolbar) {
                // 长按 word selection 产生的非折叠选区,但用户已经主动 hide 了工具栏
                // (短按同位置) → 不应该让工具栏又冒出来
                host.clearActive(state)
            }
            // else: 长按 word selection,showingContextToolbar = true → 保持工具栏显示
        } else if (!showingContextToolbar) {
            host.clearActive(state)
        }
    }

    DisposableEffect(state, floatingToolbarHost) {
        onDispose {
            floatingToolbarHost?.clearActive(state)
        }
    }

    fun showContextToolbar() {
        isDragging = false
        showingContextToolbar = true
        state.suppressedToolbarSelection = null
        // 无条件记录"工具栏已显示"对应的锚点光标和 tap 计数,
        // 这样长按触发的 word selection(selection 变成非折叠)也不会让 toggle 失效
        if (platformAdapter.requiresCollapsedCursorContextToolbarToggle) {
            androidLastCollapsedCursor = state.textFieldValue.selection.start
            androidSameCursorTapCount = 2
        }
        floatingToolbarHost?.reportActive(activeInfo())
    }

    fun hideContextToolbar() {
        showingContextToolbar = false
        state.dismissToolbar()
        floatingToolbarHost?.clearActive(state)
    }

    fun handleAndroidCollapsedCursorPress(localOffset: Offset): Boolean {
        if (!platformAdapter.requiresCollapsedCursorContextToolbarToggle) return false
        val selection = state.textFieldValue.selection
        val targetCursor = textLayoutResult
            ?.getOffsetForPosition(localOffset)
            ?: selection.start
        // 折叠选区 + 点击位置与光标不同 → 移动光标,直接 hide
        if (selection.collapsed && targetCursor != selection.start) {
            androidLastCollapsedCursor = targetCursor
            androidSameCursorTapCount = 0
            hideContextToolbar()
            return false
        }
        if (androidLastCollapsedCursor == targetCursor) {
            androidSameCursorTapCount += 1
        } else {
            androidLastCollapsedCursor = targetCursor
            androidSameCursorTapCount = 1
        }
        if (androidSameCursorTapCount >= 2) {
            // 用 host 的 active 状态作为"工具栏是否真的在显示"的真实依据,
            // 因为 LaunchedEffect 在非折叠分支可能重置过 showingContextToolbar,
            // 但 host.active 仍然指向 state(被 LaunchedEffect 再次 report 上去)
            val isToolbarShown = showingContextToolbar ||
                floatingToolbarHost?.active?.state === state
            if (isToolbarShown) {
                hideContextToolbar()
            } else {
                showContextToolbar()
            }
            return true
        }
        return false
    }

    // 缓存 CallbackTextContextMenuProvider 实例,供 CompositionLocalProvider 和
    // Press handler 共同引用,这样 Press handler 可以在 down 时调 suppressNext()
    // 吞掉 BasicTextField 紧跟着会触发的 long-press → showTextContextMenu。
    val contextMenuProvider = remember { CallbackTextContextMenuProvider(::showContextToolbar) }
    val textToolbarImpl = remember { CallbackTextToolbar(::showContextToolbar) }

    Box(modifier = modifier) {
        CompositionLocalProvider(
            LocalTextToolbar provides textToolbarImpl,
            LocalTextContextMenuToolbarProvider provides contextMenuProvider,
            LocalTextContextMenuDropdownProvider provides contextMenuProvider,
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(config.textField.innerPadding)
                    .then(
                        Modifier.suppressNativeContextMenu(
                            platformAdapter = platformAdapter,
                            onContextMenuRequested = ::showContextToolbar,
                        )
                    )
                    .pointerInput(Unit) {
                        awaitPointerEventScope {
                            while (true) {
                                val event = awaitPointerEvent(PointerEventPass.Initial)
                                if (event.changes.any { it.isConsumed }) continue
                                when (event.type) {
                                    PointerEventType.Press -> {
                                        val pressOffset = event.changes.firstOrNull()?.position
                                        // 工具栏当前已显示的话,先把 BasicTextField 紧跟着会
                                        // 触发的 long-press → showTextContextMenu 吞掉,
                                        // 否则 BasicTextField 会在 long-press timeout 后
                                        // 重新调 onShow() 把刚 hide 掉的工具栏又显示回来。
                                        if (showingContextToolbar ||
                                            floatingToolbarHost?.active?.state === state
                                        ) {
                                            contextMenuProvider.suppressNext()
                                            state.suppressedToolbarSelection = state.textFieldValue.selection.takeIf { !it.collapsed }
                                        }
                                        // 新一次 down 开始,重置 userDraggedSelection
                                        // (让 LaunchedEffect 重新基于本次 down 的行为判断)
                                        userDraggedSelection = false
                                        if (pressOffset == null || !handleAndroidCollapsedCursorPress(pressOffset)) {
                                            showingContextToolbar = false
                                            isDragging = true
                                        }
                                    }
                                    PointerEventType.Release -> {
                                        isDragging = false
                                    }
                                    PointerEventType.Move -> {}
                                }
                            }
                        }
                    }
            ) {
                // IME 拦截器接管 IME 通道(`InterceptPlatformTextInput` 替换
                // `LocalTextInputService`)。在 Android 端包装 InputConnection,
                // iOS 端包装 onEditCommand,识别 IME 主动粘贴(软键盘"粘贴"按钮、
                // 第三方输入法工具栏等)→ 还原 emoji 短码 / spans。普通富文本
                // 不需要 Block JSON 处理,`onPasteJson` 传 null,只走 1 级 emoji 还原。
                InterceptImePasteTextInput(
                    richTextState = state,
                    onPasteJson = null,
                ) {
                    RichTextBasicTextField(
                        state = state,
                        config = config,
                        placeholder = placeholder,
                        focusRequester = focusRequester,
                    onValueChange = { newValue ->
                        val oldValue = state.textFieldValue
                        if (platformAdapter.requiresCollapsedCursorContextToolbarToggle && newValue.selection.collapsed && newValue.selection.start != oldValue.selection.start) {
                            androidLastCollapsedCursor = newValue.selection.start
                            androidSameCursorTapCount = 1
                            showingContextToolbar = false
                            state.suppressedToolbarSelection = null
                        }
                        if (newValue.selection.collapsed) {
                            state.suppressedToolbarSelection = null
                        }
                        if (isDragging && !newValue.selection.collapsed) {
                            userDraggedSelection = true
                        }
                        val consumed = onUserValueChange?.invoke(oldValue, newValue) == true
                        if (!consumed) {
                            state.onValueChange(newValue, platformAdapter.supportsImeComposition)
                        }
                    },
                    onTextLayoutResult = {
                        textLayoutResult = it
                        onTextLayout(it)
                    },
                    onFocusChanged = { focused ->
                        isTextFieldFocused = focused
                        // 用户主动聚焦时记录"曾经聚焦过"标志,供调用方决定
                        // 是否在 emoji 插入 / dialog 关闭后调用 requestFocus()(避免
                        // 无焦点时拉起软键盘)。与 :blockrichtext 的
                        // BlockState.hasUserFocusedAnyField 行为一致。
                        if (focused) {
                            state.hasUserFocusedThisField = true
                        }
                        onFocusChanged(focused)
                        if (!focused) {
                            showingContextToolbar = false
                            isDragging = false
                            userDraggedSelection = false
                            state.suppressedToolbarSelection = null
                            androidLastCollapsedCursor = null
                            androidSameCursorTapCount = 0
                            floatingToolbarHost?.clearActive(state)
                        }
                    },
                    onPositioned = { position, widthPx, heightPx ->
                        textFieldWidthPx = widthPx
                        textFieldHeightPx = heightPx
                        textFieldGlobalPosition = position
                        floatingToolbarHost?.updateActivePosition(
                            state = state,
                            position = position,
                            widthPx = widthPx,
                            heightPx = heightPx,
                            anchor = toolbarAnchor(),
                        )
                    },
                    modifier = Modifier
                        .fillMaxWidth(),
                    onPreviewKeyEvent = { event ->
                        if (tryHandleEnter(event, state)) {
                            return@RichTextBasicTextField true
                        }
                        if (tryHandleDirectionKey(event, textLayoutResult, state)) {
                            return@RichTextBasicTextField true
                        }
                        handleRichTextKeyEvent(
                            event = event,
                            state = state,
                            clipboardActions = clipboardActions,
                            coroutineScope = coroutineScope,
                            allowSmartPaste = true,
                            dispatchRegisteredCommands = dispatchRegisteredCommands,
                            onUndo = onUndo,
                            onRedo = onRedo
                        )
                    }
                )
                }  // close InterceptImePasteTextInput
            }
        }
    }
}

/**
 * 段落索引接管上/下方向键，绕过 [BoundaryNewlineVisualTransformation] 将不同
 * ParagraphStyle 边界的 `\n` 替换为 `\u200B`(ZWSP) 导致的几何映射失效。
 *
 * 几何方案（[TextLayoutResult.getCursorRect] + [TextLayoutResult.getOffsetForPosition]）
 * 依赖文本布局行结构，但 ZWSP 不产生换行，滤镜后文本布局行数不足，
 * `getOffsetForPosition(targetY)` 无法映射到目标行——本函数改为纯文本级段落导航，
 * 通过 [ParagraphStyleUtils.paragraphRanges] 直接定位段起始偏移，不依赖布局。
 *
 * 块级边界（光标在首段按上 / 末段按下）不处理，返回 false 让外层
 * [com.taocent.simple.compose.component.blockrichtext.internal.block.TextBlockItem]
 * 的 TableBlock 导航接管。
 */
internal fun tryHandleDirectionKey(
    event: androidx.compose.ui.input.key.KeyEvent,
    @Suppress("UNUSED_PARAMETER") layoutResult: TextLayoutResult?,
    state: RichTextState,
): Boolean {
    if (event.type != KeyEventType.KeyDown) return false
    val selection = state.textFieldValue.selection
    val text = state.plainText
    val textLen = text.length
    val ranges = ParagraphStyleUtils.paragraphRanges(text)

    when (event.key) {
        Key.DirectionUp -> {
            if (event.isShiftPressed) return false
            val cursorOffset = if (selection.collapsed) selection.start else selection.min
            if (cursorOffset <= 0) return false
            val currentIndex = paragraphIndexForOffset(ranges, cursorOffset)
                .coerceAtMost(ranges.lastIndex)
            if (currentIndex <= 0) return false
            val currentPara = ranges[currentIndex]
            if (currentPara.start == currentPara.end) return false
            val column = (cursorOffset - currentPara.start).coerceAtLeast(0)
            val targetPara = ranges[currentIndex - 1]
            val targetContentLen = paragraphContentLen(text, targetPara)
            val targetOffset = targetPara.start + column.coerceAtMost(targetContentLen)
            if (targetOffset < cursorOffset) {
                state.moveCursorTo(targetOffset)
            }
            return true
        }
        Key.DirectionDown -> {
            if (event.isShiftPressed) return false
            val cursorOffset = if (selection.collapsed) selection.start else selection.max
            if (cursorOffset >= textLen) return false
            val currentIndex = paragraphIndexForOffset(ranges, cursorOffset)
                .coerceAtMost(ranges.lastIndex)
            if (currentIndex >= ranges.lastIndex) return false
            val currentPara = ranges[currentIndex]
            if (currentPara.start == currentPara.end) return false
            val column = (cursorOffset - currentPara.start).coerceAtLeast(0)
            val targetPara = ranges[currentIndex + 1]
            val targetContentLen = paragraphContentLen(text, targetPara)
            val targetOffset = targetPara.start + column.coerceAtMost(targetContentLen)
            if (targetOffset > cursorOffset) {
                state.moveCursorTo(targetOffset)
            }
            return true
        }
        else -> return false
    }
}

private fun paragraphIndexForOffset(ranges: List<TextRange>, offset: Int): Int {
    return ranges.indexOfFirst { offset >= it.start && offset < it.end }
        .let { if (it >= 0) it else ranges.lastIndex }
}

private fun paragraphContentLen(text: String, range: TextRange): Int {
    val len = range.end - range.start
    return if (len > 0 && text.getOrNull(range.end - 1) == '\n') (len - 1).coerceAtLeast(0) else len
}

/**
 * 接管 Enter 键换行，绕过 [BoundaryNewlineVisualTransformation] 将不同 ParagraphStyle 边界的
 * `\n` 替换为 `\u200B`(ZWSP) 导致的平台换行检测混乱。
 *
 * ZWSP 让平台文本引擎（web contenteditable / iOS UITextField）对新换行符的插入位置
 * 产生误判，表现：首次 Enter 无反应、需要按两次。本函数在 BasicTextField 处理之前拦截
 * Enter，直接调 [RichTextState.insertText] 插入 `\n`，不经过平台引擎。
 *
 * IME 组合态期间（[RichTextState.textFieldValue.composition] != null）不拦截，
 * 让 IME 自行处理候选词确认等场景。
 */
internal fun tryHandleEnter(
    event: androidx.compose.ui.input.key.KeyEvent,
    state: RichTextState,
): Boolean {
    if (event.type != KeyEventType.KeyDown) return false
    if (event.key != Key.Enter) return false
    if (state.textFieldValue.composition != null) return false
    state.insertText("\n")
    return true
}
